import React, { useMemo } from 'react'
import { Button } from 'antd'
import moment from 'moment'
import { IPlotVariable, RosePlotChart } from '../../../../../../types/analytics'
import { IBestDataset } from '../../../../../../types/analytics'
import { IGetRosePlotV2ApiPayload } from '../../../../../../types/chart'
import { IAnalytcisUserInput } from '../../../../../../types/IAnalyticsUserInput'
import { useRosePlotParams } from '../../../hooks/useRosePlotParams'
import { RosePlotInputForm } from "./RosePlotInputForm"
import { RosePlotChart as RosePlotChartComponent } from "./RosePlotChart"
import { RosePlotAdditionalControls } from "./RosePlotAdditionalInput"
import { AnalyticsSection } from "../../Layout/AnalyticsSection"

interface RosePlotProps {
  onDatasetChange: (item: string) => void
  previewDatasetId: string
  previewDatasetTimeRange: [moment.Moment, moment.Moment]
  globalDateRange: any[]
  setGlobalDateRange: (item: any) => void
  isForAllAnalytics: boolean
  setIsForAllAnalytics: (item: any) => void
  datasets: IBestDataset[]
  defaultDirectionShortName?: string
  defaultValueShortName?: string
  variables: IPlotVariable[]
  dateRange: string[]
  plotData?: RosePlotChart
  plotDefaultInterval: number
  updatedRosePlotData: RosePlotChart | null
  pointProps: string
  pointName: string
  userInput: IAnalytcisUserInput
  errorUpdate: string
  previewError: string
  updating: boolean
  onUpdateRosePlot: (values: IGetRosePlotV2ApiPayload) => void
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>
  onChangeActiveKey: (item: any) => void
  pointOption: { label: string, value: string }[]
  setUpdatedRosePlotData: (item: any) => void
  activePlot?: string
  isOffline: boolean
}

export default function RosePlot(props: RosePlotProps) {
  const {
    errorUpdate,
    updating,
    pointName,
    previewError,
    isOffline
  } = props

  const {
    rosePlotData,
    rosePlotSeries,
    variable,
    direction,
    variables: filteredVariables,
    directions,
    legendInterval,
    directionNo,
    directionChartNo,
    startDate,
    endDate,
    unitX,
    downloadName,
    degreeIntervals,
    categories,
    memoCoords,
    selectedDatasetInfo,
    doStartDateChange,
    doEndDateChange,
    doChangeDirectionNo,
    doChangeIntervalNo,
    handleCheckboxChange,
    doVariableChange,
    doDirectionChange,
    onUpdateRosePlot,
    disabledStartDate,
    disabledEndDate,
  } = useRosePlotParams(props)

  const plotInputContent = useMemo(() => (
    <RosePlotInputForm
      datasets={props.datasets}
      previewDatasetId={props.previewDatasetId}
      onDatasetChange={props.onDatasetChange}
      categories={categories}
      variables={filteredVariables}
      variable={variable}
      onVariableChange={doVariableChange}
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={doStartDateChange}
      onEndDateChange={doEndDateChange}
      disabledStartDate={disabledStartDate}
      disabledEndDate={disabledEndDate}
      isForAllAnalytics={props.isForAllAnalytics}
      onCheckboxChange={handleCheckboxChange}
      isLoading={updating}
      hasError={!!previewError}
    />
  ), [
    props.datasets,
    props.previewDatasetId,
    props.onDatasetChange,
    categories,
    filteredVariables,
    variable,
    doVariableChange,
    startDate,
    endDate,
    doStartDateChange,
    doEndDateChange,
    disabledStartDate,
    disabledEndDate,
    props.isForAllAnalytics,
    handleCheckboxChange,
    updating,
    previewError
  ])

  const updateContent = useMemo(() => (
    <div style={{
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '36px'
    }}>
      <Button
        type="primary"
        id="Analytics-RosePlot-Update_Button"
        disabled={!!previewError}
        loading={updating}
        onClick={() => onUpdateRosePlot()}
        style={{
          width: '90px',
          height: '36px'
        }}
      >
        Update
      </Button>
    </div>
  ), [
    previewError,
    updating,
    onUpdateRosePlot
  ])

  const chartContent = useMemo(() => (
    <RosePlotChartComponent
      rosePlotData={rosePlotData}
      rosePlotSeries={rosePlotSeries}
      pointName={pointName}
      coordinates={memoCoords}
      directionChartNo={directionChartNo}
      unitX={unitX}
      downloadName={downloadName ?? ''}
      selectedDatasetInfo={selectedDatasetInfo}
      isLoading={updating}
      error={errorUpdate || previewError}
      isOffline={isOffline}
    />
  ), [
    rosePlotData,
    rosePlotSeries,
    pointName,
    memoCoords,
    directionChartNo,
    unitX,
    downloadName,
    selectedDatasetInfo,
    updating,
    errorUpdate,
    previewError
  ])

  const additionalContent = useMemo(() => (
    <RosePlotAdditionalControls
      directions={directions}
      direction={direction}
      onDirectionChange={doDirectionChange}
      directionNo={directionNo}
      degreeIntervals={degreeIntervals}
      onChangeDirectionNo={doChangeDirectionNo}
      legendInterval={legendInterval}
      onChangeIntervalNo={doChangeIntervalNo}
      isLoading={updating}
      hasError={!!previewError}
    />
  ), [
    directions,
    direction,
    doDirectionChange,
    directionNo,
    degreeIntervals,
    doChangeDirectionNo,
    legendInterval,
    doChangeIntervalNo,
    updating,
    previewError
  ])

  return (
    <AnalyticsSection
      plotInputContent={plotInputContent}
      updateContent={updateContent}
      chartContent={chartContent}
      additionalContent={additionalContent}
      isLoading={updating}
    />
  )
}
